/**
 * The opener side of the RudderStack account OAuth relay (spec §4.2 "What the opener does").
 *
 * Invariants, each one a review finding or a harness result:
 *  - `window.open` runs synchronously inside the click handler (popup blockers need the gesture),
 *    BEFORE the `oauthStart` request; `popup.opener` is nulled — Lookout never needs it, and a consent
 *    page without COOP could otherwise navigate this tab.
 *  - Every broadcast is nonce-checked FIRST, whatever its type. A message from another tab, a
 *    replay, or a crafted link to `/oauth/rudderstack` is dropped before its `error` or `code` is read.
 *  - `popup.closed` is never read: after a COOP swap it reports `true` while the consent page is
 *    still open. Abandonment is the 10-minute timer or an explicit cancel, both reported through
 *    `oauthAbandon`.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';
import { RUDDERSTACK_REAUTH_MESSAGE } from '@/_shared/lib/rudderstack-reauth';

import {
  RUDDERSTACK_OAUTH_CHANNEL,
  RUDDERSTACK_OAUTH_MESSAGE_TYPE,
  type RudderstackOauthMessage,
} from '../../../auth/views/RudderstackOauthLanding';
import { decodeOauthState } from './oauthState';

export type RudderstackOauthStatus = 'idle' | 'starting' | 'waiting' | 'done' | 'error' | 'blocked';

/**
 * The `oauthStart` input as the server declares it: a union of a create payload
 * (`{ accountDefinitionName, name, options, surface }`) and a reauth payload (`{ accountId, surface }`)
 * — Task 6 widened the RPC input to this union (spec §5 reauth arm).
 */
type OauthStartInput = Parameters<RouterClient<AppRouter>['settings']['rudderstackAccounts']['oauthStart']>[0];
/** `'activation' | 'settings'` — indexed off the union rather than re-declared (both arms carry it). */
type RsAccountSurface = OauthStartInput['surface'];

/**
 * The CLIENT's view of the create-or-reauth union — deliberately NOT a bare mirror of
 * `OauthStartInput`. Both arms carry `accountDefinitionName` so every abandon/telemetry callsite
 * (provider_error, popup_blocked, timeout, cancel) can read it unconditionally regardless of which
 * arm started the flow; on the reauth arm it is TELEMETRY-ONLY and must never reach the wire — the
 * wire payload is built by hand per arm in `start`/`onMessage`, never by spreading `input`. `kind`
 * itself is also never sent on the wire (task-12-corrections.md).
 */
export type StartInput =
  | {
      kind: 'create';
      accountDefinitionName: string;
      name: string;
      options?: Record<string, unknown>;
      surface: RsAccountSurface;
    }
  | { kind: 'reauth'; accountId: string; accountDefinitionName: string; surface: RsAccountSurface };

const ABANDON_AFTER_MS = 10 * 60 * 1000;

interface Flow {
  popup: Window;
  nonce: string;
  flowId: string;
  input: StartInput;
  startedAt: number;
  channel: BroadcastChannel;
  timer: number;
  /** The provider authorize URL this flow navigated to — retained so `reopen()` can re-open the SAME
   *  flow rather than minting a new one (design follow-up 3). */
  consentUrl: string;
}

export function useRudderstackOAuth(): {
  status: RudderstackOauthStatus;
  accountId: string | null;
  error: string | null;
  start: (input: StartInput) => Promise<void>;
  cancel: () => void;
  reopen: () => void;
  reset: () => void;
} {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const { toastIfReauth } = useRudderstackSignInAgain();
  const startMutation = useMutation(orpc.settings.rudderstackAccounts.oauthStart.mutationOptions());
  const completeMutation = useMutation(
    orpc.settings.rudderstackAccounts.oauthComplete.mutationOptions({
      // Invalidation lives HERE, on the mutation itself, not only in the opener's completion effect
      // (M2). The consent round trip outlives its opener routinely — the picker sheet closes, a route
      // changes, a drawer unmounts — and TanStack drops the per-call `mutate(vars, { onSuccess })`
      // callbacks when the calling component unmounts, while still running the mutation's own. The
      // platform has minted or rotated the account by then either way, so a cache that never learns
      // about it shows a stale list until something else happens to refetch. The opener's effect stays
      // for what only it can do (its toast, its `onReauthorized`).
      //
      // All account-backed query keys, always (shared UI notes §11): settings, activation picker, and
      // audience detail bindings read the same accounts.
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
      },
    }),
  );
  const abandonMutation = useMutation(orpc.settings.rudderstackAccounts.oauthAbandon.mutationOptions());

  const [status, setStatus] = useState<RudderstackOauthStatus>('idle');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const flowRef = useRef<Flow | null>(null);
  // A generation counter: cancel/reset bump it, and a `start()` that resumes after `oauthStart`
  // resolves checks it before touching anything (PR #731 review — cancel during `starting`).
  const genRef = useRef(0);
  // The blank popup between `window.open` and navigation, so cancel can close it before a flow exists.
  const pendingRef = useRef<{ popup: Window; accountDefinitionName: string } | null>(null);

  const teardown = useCallback((closePopup: boolean) => {
    const flow = flowRef.current;
    if (!flow) return;
    flowRef.current = null;
    window.clearTimeout(flow.timer);
    flow.channel.close();
    if (closePopup) {
      try {
        flow.popup.close(); // a no-op after a COOP swap; harmless
      } catch {
        /* the popup may already be gone */
      }
    }
  }, []);

  const abandon = useCallback(
    (input: {
      accountDefinitionName: string;
      flowId?: string;
      reason: 'timeout' | 'cancel' | 'popup_blocked' | 'provider_error';
      errorClass?: string;
    }) => {
      abandonMutation.mutate(input);
    },
    [abandonMutation],
  );

  const onMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      const flow = flowRef.current;
      if (!flow) return;
      const data = event.data as Partial<RudderstackOauthMessage> | null;
      if (data?.type !== RUDDERSTACK_OAUTH_MESSAGE_TYPE || !data.payload) return;
      // Nonce first, for every message type.
      const state = decodeOauthState(data.payload.state);
      if (state?.n !== flow.nonce) return;

      // Telemetry-only field, present on both arms — never spread onto the wire below.
      const { accountDefinitionName, surface } = flow.input;
      if (data.payload.error || !data.payload.code) {
        const errorClass = data.payload.error ?? 'missing_code';
        abandon({ accountDefinitionName, flowId: flow.flowId, reason: 'provider_error', errorClass });
        setError(
          errorClass === 'access_denied'
            ? 'Access was denied on the provider side.'
            : `The provider returned an error (${errorClass}).`,
        );
        setStatus('error');
        teardown(true);
        return;
      }

      const elapsedMs = Date.now() - flow.startedAt;
      teardown(true);
      // Built by hand, per arm — never `{ ...flow.input }`. `kind` and (on reauth) `accountDefinitionName`
      // must not reach the wire.
      const wirePayload =
        flow.input.kind === 'create'
          ? {
              accountDefinitionName: flow.input.accountDefinitionName,
              name: flow.input.name,
              options: flow.input.options ?? {},
            }
          : { accountId: flow.input.accountId };
      completeMutation.mutate(
        {
          ...wirePayload,
          surface,
          search: data.payload.search,
          flowId: flow.flowId,
          elapsedMs,
        },
        {
          onSuccess: (out) => {
            setAccountId(out.accountId);
            setStatus('done');
          },
          onError: (err) => {
            // `data.message`, not `err.message`: a platform rejection's own text rides the redacted
            // data channel now, because the top-level message is logged verbatim (orpc-errors.ts).
            setError(
              toastIfReauth(err)
                ? RUDDERSTACK_REAUTH_MESSAGE
                : orpcErrorMessage(err, 'Could not finish connecting the account.'),
            );
            setStatus('error');
          },
        },
      );
    },
    [abandon, completeMutation, teardown, toastIfReauth],
  );

  const start = useCallback(
    async (input: StartInput): Promise<void> => {
      setError(null);
      setAccountId(null);
      // Any earlier flow is over: its timer must not fire into this one.
      teardown(true);
      const gen = ++genRef.current;
      // Synchronously, in the user's gesture. Everything async comes after.
      const popup = window.open('', '_blank');
      if (!popup) {
        abandon({ accountDefinitionName: input.accountDefinitionName, reason: 'popup_blocked' });
        setStatus('blocked');
        return;
      }
      try {
        popup.opener = null;
      } catch {
        /* some browsers make opener read-only on about:blank; nothing depends on it */
      }
      pendingRef.current = { popup, accountDefinitionName: input.accountDefinitionName };
      setStatus('starting');
      // Set once oauthStart resolves, so the outer catch can still abandon a flow the backend has
      // already logged (`rs_oauth_started`) even though `flowRef.current` is assigned only after the
      // popup navigates — a failure between those two points must not orphan the started flow.
      let startedFlowId: string | undefined;
      try {
        // Built by hand, per arm — never `{ ...input }`. `kind` and (on reauth) `accountDefinitionName`
        // must not reach the wire; a naive spread would send `{ accountId, surface, options: {} }` to a
        // server branch that expects exactly `{ accountId, surface }`.
        const wireInput =
          input.kind === 'create'
            ? {
                accountDefinitionName: input.accountDefinitionName,
                name: input.name,
                options: input.options ?? {},
                surface: input.surface,
              }
            : { accountId: input.accountId, surface: input.surface };
        const out = await startMutation.mutateAsync(wireInput);
        if (genRef.current !== gen) return; // cancelled while oauthStart was in flight; popup already closed
        startedFlowId = out.flowId;
        const channel = new BroadcastChannel(RUDDERSTACK_OAUTH_CHANNEL);
        channel.onmessage = onMessage;
        const timer = window.setTimeout(() => {
          const flow = flowRef.current;
          if (!flow) return;
          abandon({ accountDefinitionName: flow.input.accountDefinitionName, flowId: flow.flowId, reason: 'timeout' });
          setError('Timed out waiting for the provider. Try again.');
          setStatus('error');
          teardown(true);
        }, ABANDON_AFTER_MS);
        try {
          popup.location.href = out.url;
        } catch (err) {
          // Nothing is a flow until the popup is on its way: drop the listener and timer we just made.
          window.clearTimeout(timer);
          channel.close();
          throw err;
        }
        flowRef.current = {
          popup,
          nonce: out.nonce,
          flowId: out.flowId,
          input,
          startedAt: Date.now(),
          channel,
          timer,
          consentUrl: out.url,
        };
        pendingRef.current = null;
        setStatus('waiting');
      } catch (err) {
        if (genRef.current !== gen) return;
        pendingRef.current = null;
        try {
          popup.close();
        } catch {
          /* ignore */
        }
        // oauthStart already resolved (the backend logged rs_oauth_started) before this branch was
        // reached — e.g. `popup.location.href` throwing because the blank popup was already closed —
        // so that flow needs an explicit end. Otherwise it looks exactly like the observability spec's
        // own alert signature: a start with no completion inside fifteen minutes. `flowRef.current` is
        // never set on this path (only after a successful navigation), so this is the only place that
        // can still close it out; mutually exclusive with cancel()/the timeout callback, which only run
        // once `flowRef.current` exists — no double-abandon.
        if (startedFlowId !== undefined) {
          abandon({
            accountDefinitionName: input.accountDefinitionName,
            flowId: startedFlowId,
            reason: 'popup_blocked',
          });
        }
        setError(
          toastIfReauth(err) ? RUDDERSTACK_REAUTH_MESSAGE : orpcErrorMessage(err, 'Could not start the connection.'),
        );
        setStatus('error');
      }
    },
    [abandon, onMessage, startMutation, teardown, toastIfReauth],
  );

  /**
   * Continue the SAME flow by reopening its consent URL in a fresh popup — the fix for someone who
   * closed the consent window by accident. Calling `start()` again is NOT this: it mints a new flow
   * server-side, opens a second popup and overwrites the flow record, orphaning the first attempt's
   * channel and timer — a started flow with no completion or abandon, the exact alert signature fixed
   * a week's worth of work ago. `reopen()` mints nothing: the flow id, nonce and timer are untouched
   * (per the file header's `genRef`/`pendingRef` discipline note — neither is touched here either,
   * since a reopen only ever runs once a flow already exists past `starting`), and only the popup
   * handle changes — the original may already be closed, which is the whole reason to reopen, so a
   * closed window cannot simply be renavigated and a fresh `window.open` is required.
   *
   * A no-op when there is no live flow (idle, done, error, or still `starting`): there is nothing to
   * continue, so nothing opens. Blocked (`window.open` returns null, or the freshly-opened blank popup
   * refuses navigation): the flow is left exactly as it was — Cancel and Reopen both stay available,
   * rather than ending a flow the user might still complete once they allow popups. See `onMessage` for
   * how the "second return" case (the first window already completed before this one is reopened) is
   * handled — the SAME nonce-scoped teardown every completion goes through, unmodified here.
   */
  const reopen = useCallback((): void => {
    const flow = flowRef.current;
    if (!flow) return;
    // Synchronously, in the user's gesture — same popup-blocker discipline as start().
    const popup = window.open('', '_blank');
    if (!popup) return;
    try {
      popup.opener = null;
    } catch {
      /* some browsers make opener read-only on about:blank; nothing depends on it */
    }
    try {
      popup.location.href = flow.consentUrl;
    } catch {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      return;
    }
    flow.popup = popup;
  }, []);

  /** Close the blank popup of a flow that has not navigated yet (cancel/reset during `starting`). */
  const dropPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return null;
    try {
      pending.popup.close();
    } catch {
      /* ignore */
    }
    return pending;
  }, []);

  const cancel = useCallback(() => {
    genRef.current += 1;
    const flow = flowRef.current;
    const pending = dropPending();
    if (flow) {
      abandon({ accountDefinitionName: flow.input.accountDefinitionName, flowId: flow.flowId, reason: 'cancel' });
    } else if (pending) {
      abandon({ accountDefinitionName: pending.accountDefinitionName, reason: 'cancel' });
    }
    teardown(true);
    setStatus('idle');
  }, [abandon, dropPending, teardown]);

  const reset = useCallback(() => {
    genRef.current += 1;
    dropPending();
    teardown(true);
    setStatus('idle');
    setAccountId(null);
    setError(null);
  }, [dropPending, teardown]);

  // Unmount: drop the listener and timer; do not report — the page is going away, not the flow.
  useEffect(() => () => teardown(false), [teardown]);

  return { status, accountId, error, start, cancel, reopen, reset };
}
